import { useCallback, useEffect, useMemo } from 'react';
import {
  buildCheckInPassage,
  solidRepertoireLabel,
} from '../lib/wpmCheckIn';
import type { KeyboardLayoutId, RoundResult, UserProfile, WpmCheckIn } from '../types';
import { useRoundInput } from '../lib/useRoundInput';
import ParagraphTextDisplay from './ParagraphTextDisplay';
import Keyboard from './Keyboard';

const MAX_DURATION_MS = 90_000;

interface WpmCheckInScreenProps {
  user: UserProfile;
  keyboardLayout: KeyboardLayoutId;
  textSeed?: number;
  onFinish: (checkIn: WpmCheckIn) => void;
  onCancel: () => void;
}

export default function WpmCheckInScreen({
  user,
  keyboardLayout,
  textSeed,
  onFinish,
  onCancel,
}: WpmCheckInScreenProps) {
  const seed = textSeed ?? Date.now();
  const { text, solidKeys } = useMemo(
    () => buildCheckInPassage(user, seed),
    [user, seed],
  );

  const handleRoundFinish = useCallback(
    (r: RoundResult) => {
      onFinish({
        wpm: r.wpm,
        accuracy: r.accuracy,
        correctChars: r.correctChars,
        totalKeystrokes: r.totalKeystrokes,
        backspaces: r.backspaces,
        durationMs: r.durationMs,
        takenAt: new Date().toISOString(),
        solidKeyFingerprint: [...solidKeys].sort().join(''),
        solidKeyCount: solidKeys.filter((k) => k !== ' ').length,
        textSeed: seed,
      });
    },
    [onFinish, seed, solidKeys],
  );

  const input = useRoundInput({
    targetText: text,
    mode: 'challenge',
    roundId: 'wpm-check-in',
    roundTitle: 'Hastighedstest',
    presentationKind: 'standard',
    onFinish: handleRoundFinish,
  });

  const { finish, finished, elapsedMs } = input;
  useEffect(() => {
    if (finished || elapsedMs < MAX_DURATION_MS) return;
    finish(false);
  }, [elapsedMs, finished, finish]);

  if (!text) {
    return (
      <div className="mx-auto max-w-lg text-center">
        <p className="text-sm text-muted">
          Du skal gennemføre mindst ét trin, før du kan tage hastighedstesten.
        </p>
        <button type="button" onClick={onCancel} className="btn-secondary mt-4">
          Tilbage
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header>
        <p className="label">Hastighedstest</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Skriv teksten nedenfor
        </h1>
        <p className="mt-2 text-sm text-muted">
          Samme måling hver gang: kun taster og ord fra trin du har gennemført
          ({solidRepertoireLabel(user)}). Skriv i et jævnt tempo — testen stopper
          efter 90 sekunder eller når teksten er færdig.
        </p>
      </header>

      <div className="grid grid-cols-4 gap-3">
        <Stat label="Tid" value={formatMs(input.elapsedMs)} />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
        <Stat
          label="Nøjagtighed"
          value={`${(input.liveAcc * 100).toFixed(0)}%`}
        />
        <Stat
          label="Tegn"
          value={`${input.cursor} / ${text.length}`}
        />
      </div>

      <ParagraphTextDisplay
        text={text}
        cursor={input.cursor}
        correctMask={input.correctMask}
      />

      <Keyboard
        layoutId={keyboardLayout}
        nextExpectedKey={input.expectedChar}
        lastPressedKey={input.lastPress?.key}
        lastPressCorrect={input.lastPress?.correct}
        unlockedKeys={solidKeys}
        hintLevel="none"
        showFingerDot={false}
      />

      <div className="flex justify-between gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annuller
        </button>
        <p className="self-center text-xs text-faint">
          Max {MAX_DURATION_MS / 1000} sek · Escape afslutter
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface p-3 text-center">
      <p className="text-xs text-faint">{label}</p>
      <p className="mt-0.5 font-mono text-lg font-semibold text-strong">
        {value}
      </p>
    </div>
  );
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}
